import { m } from 'framer-motion';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import coreAxios from 'src/utils/core-axios';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
//
import NotificationItem from './notification-item';

// ----------------------------------------------------------------------

// Tabs are now dynamic, not hardcoded

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const navigate = useNavigate();
  const drawer = useBoolean();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const bellRef = useRef(null);

  const smUp = useResponsive('up', 'sm');

  const [notifications, setNotifications] = useState([]);
  const [currentTab, setCurrentTab] = useState('all');

  // Dynamic tab counts
  const unreadCount = notifications.filter(n => !n.is_read).length;
  const allCount = notifications.length;

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const [totalUnRead, setTotalUnRead] = useState(0);

  // Get userType, userId, and token from localStorage
  const getUserAuth = () => {
    try {
      const userDetails = JSON.parse(localStorage.getItem('loginDetails'));
      const userType = userDetails?.user_type || userDetails?.logindata?.user_type || 'admin';
      const userId = userDetails?.logindata?.id || userDetails?.id || '';
      const token = userDetails?.token || userDetails?.logindata?.token || '';
      return { userType, userId, token };
    } catch {
      return { userType: 'admin', userId: '', token: '' };
    }
  };

  // Fetch notifications and unread count
  const fetchNotifications = useCallback(async () => {
    try {
      const { userType, userId, token } = getUserAuth();
      const limit = 20;
      const offset = 0;
      const res = await coreAxios.get(`/api/core/notifications`, {
        params: { userType, userId, limit, offset },
        headers: { Authtoken: token },
      });
      if (!res.data.Error) {
        setNotifications(res.data.Details.notifications || []);
        setTotalUnRead(res.data.Details.unread || 0);
      } else {
        setNotifications([]);
        setTotalUnRead(0);
        console.warn('[Notifications] API error:', res.data.Error);
      }
    } catch (err) {
      console.error('[Notifications] Fetch error:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Refresh when a foreground push arrives (dispatched by the dashboard layout)
  useEffect(() => {
    const handleForegroundPush = () => fetchNotifications();
    window.addEventListener('erp:push-received', handleForegroundPush);
    return () => window.removeEventListener('erp:push-received', handleForegroundPush);
  }, [fetchNotifications]);

  // Auto-open notifications drawer when receiving push click or openNotifications query
  useEffect(() => {
    const handleOpenDrawer = () => {
      fetchNotifications();
      drawer.onTrue();
    };
    window.addEventListener('erp:open-notifications', handleOpenDrawer);

    // Also check if opened from URL query ?openNotifications=true
    const params = new URLSearchParams(window.location.search);
    if (params.get('openNotifications') === 'true') {
      drawer.onTrue();
      // Clean query parameter from URL without reloading
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Listen to Service Worker postMessage
    const handleMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_NAVIGATE' || event.data?.type === 'OPEN_NOTIFICATIONS') {
        fetchNotifications();
        drawer.onTrue();
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('erp:open-notifications', handleOpenDrawer);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [fetchNotifications, drawer]);


  const handleMarkAllAsRead = async () => {
    try {
      const { userType, userId, token } = getUserAuth();
      await coreAxios.put('/api/core/notifications/read-all', { userType, userId }, {
        headers: { Authtoken: token },
      });
      fetchNotifications();
    } catch (err) {
      console.error('[Notifications] Mark all as read error:', err);
    }
  };

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      <Tab
        key="all"
        value="all"
        label="All"
        icon={<Label variant={currentTab === 'all' ? 'filled' : 'soft'} color="default">{allCount}</Label>}
        iconPosition="end"
        sx={{ mr: 3 }}
      />
      <Tab
        key="unread"
        value="unread"
        label="Unread"
        icon={<Label variant={currentTab === 'unread' ? 'filled' : 'soft'} color="info">{unreadCount}</Label>}
        iconPosition="end"
      />
    </Tabs>
  );


  const resolveNotificationUrl = (notification) => {
    const { action_url, entity_type, entity_id, code, title } = notification;
    const entityId = entity_id && entity_id !== '0' && entity_id !== 0 ? entity_id : null;
    const type = (entity_type || '').toLowerCase();
    const notifCode = (code || '').toUpperCase();
    const notifTitle = (title || '').toUpperCase();

    // GRN Created / Received notification -> Do not navigate (informational, purely marks as read)
    if (
      type === 'grn' ||
      notifCode === 'GRN_CREATED' ||
      (notifCode.includes('GRN') && !notifCode.includes('BILL') && !notifCode.includes('INVOICE')) ||
      (notifTitle.includes('GRN') && !notifTitle.includes('BILL') && !notifTitle.includes('INVOICE') && !notifTitle.includes('PAYOUT'))
    ) {
      return null;
    }

    // Project / Stage / Task deadline notifications -> navigate directly to project page
    if (
      type === 'site' ||
      type === 'stage' ||
      type === 'task' ||
      notifCode === 'PROJECT_DEADLINE_APPROACHING' ||
      notifCode === 'STAGE_DEADLINE_CROSSED' ||
      notifCode === 'STAGE_DEADLINE_REMINDER' ||
      notifCode === 'TASK_DEADLINE_CROSSED' ||
      notifCode === 'TASK_DEADLINE_REMINDER' ||
      (action_url && action_url.startsWith('/sites/'))
    ) {
      const siteIdMatch = (action_url || '').match(/^\/sites\/(\d+)/);
      let siteId = notification.site_id || 0;
      if (!siteId && siteIdMatch) {
        siteId = siteIdMatch[1];
      } else if (!siteId && type === 'site') {
        siteId = entityId || 0;
      }
      return siteId ? `/projects/${siteId}` : '/projects';
    }

    // Purchase Order & Late Delivery notifications -> navigate directly to project PO page
    if (notifCode === 'MATERIAL_DELIVERY_LATE' || type === 'purchase_order' || (action_url && action_url.startsWith('/purchase-orders/'))) {
      const poIdMatch = (action_url || '').match(/^\/purchase-orders\/(\d+)/);
      const targetPoId = entityId || (poIdMatch ? poIdMatch[1] : null);
      return targetPoId ? `/projects/purchaseorder/${targetPoId}` : '/projects';
    }

    const siteId = notification.site_id || 0;
    const baseTarget = (() => {
      // 1. Material Payout Approval (Tab 7 or deep link)
      if (type === 'material_payout' || notifCode.includes('MATERIAL_PAYOUT') || notifTitle.includes('MATERIAL PAYOUT')) {
        return entityId ? `/approvals/materialpayoutapproval/${entityId}` : '/approvals?tab=7';
      }

      // 2. Indent Approval (Tab 4 or deep link)
      if (type === 'indent' || notifCode.includes('INDENT') || notifTitle.includes('INDENT')) {
        return entityId ? `/approvals/indentapproval/${entityId}` : '/approvals?tab=4';
      }

      // 3. Purchase Order Approval (Tab 5 or deep link)
      if (type === 'purchase_order' || notifCode.includes('PO_') || notifTitle.includes('PURCHASE ORDER')) {
        return entityId ? `/approvals/purchaseorderapproval/${entityId}` : '/approvals?tab=5';
      }

      // 4. Request Order Approval (Tab 6 or deep link)
      if (type === 'request_order' || notifCode.includes('RO_') || notifTitle.includes('REQUEST ORDER')) {
        return entityId ? `/approvals/requestorderapproval/${entityId}` : '/approvals?tab=6';
      }

      // 5. Contractor Labour Payout (Tab 1)
      if (type === 'contractor_labour' || notifCode.includes('CONTRACTOR_LABOUR') || notifTitle.includes('CONTRACTOR LABOUR') || notifTitle.includes('CONTRACTOR LABOR')) {
        return '/approvals?tab=1';
      }

      // 6. Contractor Payout & Company Contractor Payout (Tab 2)
      if (type === 'contractor_payout' || notifCode.includes('CONTRACTOR') || notifTitle.includes('CONTRACTOR')) {
        return '/approvals?tab=2';
      }

      // 7. Bills / GRN Invoice Payout (Tab 3)
      if (type === 'grn_bill' || type === 'bill' || notifCode.includes('BILL') || notifCode.includes('INVOICE') || notifTitle.includes('BILL') || notifTitle.includes('INVOICE')) {
        return '/approvals?tab=3';
      }

      // 8. Company Labour Payout (Tab 0)
      if (type === 'labor_approval' || notifCode.includes('LABOUR_PAYOUT') || notifCode.includes('LABOR') || notifTitle.includes('LABOUR') || notifTitle.includes('LABOR')) {
        return '/approvals?tab=0';
      }

      // 9. If action_url points to a specific approval route or tab
      if (action_url && action_url.startsWith('/approvals')) {
        return action_url;
      }

      if (action_url && !action_url.startsWith('/purchase-orders')) {
        return action_url;
      }

      return '/approvals';
    })();

    if (siteId && !baseTarget.includes('site=')) {
      const joiner = baseTarget.includes('?') ? '&' : '?';
      return `${baseTarget}${joiner}site=${siteId}`;
    }

    return baseTarget;
  };

  const handleNotificationClick = async (notification) => {
    // Navigation logic
    const targetUrl = resolveNotificationUrl(notification);

    // Close popover and drawer if navigating
    if (targetUrl) {
      setPopoverOpen(false);
      drawer.onFalse();
    }

    // Mark as read if not already
    if (!notification.is_read && notification.id) {
      try {
        const { userType, userId, token } = getUserAuth();
        await coreAxios.put(`/api/core/notifications/read`, {
          userType,
          userId,
          notificationId: notification.id,
        }, {
          headers: { Authtoken: token },
        });
        fetchNotifications();
      } catch (err) {
        console.error('[Notifications] Mark as read error:', err);
      }
    }

    if (targetUrl) {
      navigate(targetUrl);
    }
  };

  const handleDismiss = async (notification) => {
    try {
      const { userType, userId, token } = getUserAuth();
      await coreAxios.delete(`/api/core/notifications/dismiss`, {
        data: {
          userType,
          userId,
          notificationId: notification.id,
        },
        headers: { Authtoken: token },
      });
      fetchNotifications();
    } catch (err) {
      console.error('[Notifications] Dismiss error:', err);
    }
  };

  // Filter notifications by tab
  const filteredNotifications = currentTab === 'unread'
    ? notifications.filter(n => !n.is_read)
    : notifications;

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {filteredNotifications.map((notification) => (
          <div key={notification.id} style={{ position: 'relative' }}>
            <NotificationItem
              notification={notification}
              onClick={() => handleNotificationClick(notification)}
            />
            <IconButton
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
              onClick={e => { e.stopPropagation(); handleDismiss(notification); }}
              aria-label="Dismiss notification"
            >
              <Iconify icon="eva:close-fill" width={18} />
            </IconButton>
          </div>
        ))}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        ref={bellRef}
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
        onMouseEnter={() => setPopoverOpen(true)}
        onMouseLeave={() => setTimeout(() => setPopoverOpen(false), 200)}
        aria-owns={popoverOpen ? 'notification-popover' : undefined}
        aria-haspopup="true"
        sx={{
          mr: 2,
          ml: { xs: 0, sm: -2 },
          p: 1.2,
          fontSize: 0,
          borderRadius: 2,
          boxShadow: drawer.value ? 4 : 0,
          transition: 'all 0.2s',
        }}
      >
        <Badge badgeContent={totalUnRead} color="error" sx={{
          '& .MuiBadge-badge': {
            fontSize: 14,
            height: 24,
            minWidth: 24,
            borderRadius: '50%',
            boxShadow: '0 0 0 2px #fff',
          }
        }}>
          <Iconify icon="solar:bell-bing-bold-duotone" width={32} height={32} color={drawer.value ? '#00A76F' : '#637381'} />
        </Badge>
      </IconButton>

      <Popover
        id="notification-popover"
        open={popoverOpen}
        anchorEl={bellRef.current}
        onClose={() => setPopoverOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          onMouseEnter: () => setPopoverOpen(true),
          onMouseLeave: () => setPopoverOpen(false),
          sx: { width: 340, p: 1, borderRadius: 2, boxShadow: 6 },
        }}
        disableRestoreFocus
      >
        <Typography variant="subtitle1" sx={{ px: 1, py: 1, fontWeight: 700, color: '#212B36' }}>
          Notifications
        </Typography>
        <Divider />
        <List disablePadding>
          {notifications.slice(0, 5).map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClick={() => handleNotificationClick(notification)}
            />
          ))}
        </List>
        <Box sx={{ p: 1 }}>
          <Button fullWidth size="small" variant="contained" color="primary" sx={{ fontWeight: 600, borderRadius: 1 }} onClick={() => { setPopoverOpen(false); drawer.onTrue(); }}>
            See All
          </Button>
        </Box>
      </Popover>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 440, borderTopLeftRadius: 16, borderBottomLeftRadius: 16, boxShadow: 8 },
        }}
      >
        {renderHead}

        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
          <IconButton onClick={handleMarkAllAsRead}>
            <Iconify icon="solar:settings-bold-duotone" />
          </IconButton>
        </Stack>

        <Divider />

        {renderList}

        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large" variant="contained" color="primary" sx={{ fontWeight: 600, borderRadius: 1 }}>
            See All
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
